
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Updated types that match the actual database schema
export interface LearningPath {
  id: string;
  title: string;
  description: string;
  path_type: string;
  status: string;
  creator_id: string;
  discipline_id: string;
  difficulty_level: number;
  estimated_duration_minutes: number;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  metadata: any;
}

export interface PathNode {
  id: string;
  trail_id: string;
  node_id: string;
  order_index: number;
  is_optional: boolean;
  completion_criteria: any;
  unlock_conditions: any;
  created_at: string;
  // Node details from join
  title?: string;
  content?: any;
  status?: string;
  difficulty_level?: number;
  estimated_study_time?: number;
  content_type?: string;
  node_type?: string;
}

// Get all learning paths, optionally filtered by discipline
export const useLearningPaths = (disciplineId?: string) => {
  return useQuery({
    queryKey: ['learning_paths', disciplineId],
    queryFn: async () => {
      let query = supabase
        .from('learning_paths')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false });
      
      if (disciplineId) {
        query = query.eq('discipline_id', disciplineId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as LearningPath[];
    },
  });
};

// Get nodes for a specific learning path
export const useLearningPathNodes = (pathId: string) => {
  return useQuery({
    queryKey: ['learning_path_nodes', pathId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('learning_path_items')
        .select(`
          *,
          nodes (
            id,
            title,
            content,
            status,
            difficulty_level,
            estimated_study_time,
            content_type,
            node_type
          )
        `)
        .eq('path_id', pathId)
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      
      // Transform the data to match PathNode interface
      return data?.map(item => ({
        ...item,
        trail_id: item.path_id,
        title: item.nodes?.title,
        content: item.nodes?.content,
        status: item.nodes?.status,
        difficulty_level: item.nodes?.difficulty_level,
        estimated_study_time: item.nodes?.estimated_study_time,
        content_type: item.nodes?.content_type,
        node_type: item.nodes?.node_type
      })) as PathNode[];
    },
    enabled: !!pathId,
  });
};

// Mock user progress data
export const useLearningPathProgress = (pathId: string) => {
  return useQuery({
    queryKey: ['learning_path_progress', pathId],
    queryFn: async () => {
      // Mock progress data
      const mockProgress: Record<string, { completed: boolean; timeSpent: number; rating?: number }> = {};
      
      return mockProgress;
    },
    enabled: !!pathId,
  });
};

// Hook to get learning path statistics
export const useLearningPathStats = () => {
  return useQuery({
    queryKey: ['learning_path_stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('learning_paths')
        .select('id, title, status')
        .eq('status', 'published')
        .limit(10);
      
      if (error) throw error;
      return data;
    },
  });
};

// Hook to create a new learning path
export const useCreateLearningPath = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (path: Partial<LearningPath>) => {
      const { data, error } = await supabase
        .from('learning_paths')
        .insert(path)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learning_paths'] });
    },
  });
};

// Hook to add nodes to a learning path
export const useAddNodeToPath = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ pathId, nodeId, orderIndex }: {
      pathId: string;
      nodeId: string;
      orderIndex: number;
    }) => {
      const { data, error } = await supabase
        .from('learning_path_items')
        .insert({
          path_id: pathId,
          node_id: nodeId,
          order_index: orderIndex
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['learning_path_nodes', variables.pathId] });
    },
  });
};
